import type { LucideIcon } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Superfície interna: usada quando o card já está dentro de outro card. */
  readonly sunken?: boolean | undefined;
  readonly interactive?: boolean | undefined;
  /** Quadrado: o cartão que é a própria tabela, de borda a borda (design.md §8). */
  readonly square?: boolean | undefined;
}

/**
 * Cartão: superfície com filete, plana (design.md §8).
 *
 * O filete diz onde o cartão termina; sombra não há — o conteúdo não flutua.
 * Clicável, o filete escurece um degrau sob o ponteiro. O afundado é o
 * contrário — sem filete, é um rebaixo dentro do cartão.
 */
export function Card({ sunken, interactive, square, className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        !square && 'rounded-lg',
        sunken ? 'bg-sunken' : 'border border-line bg-surface',
        interactive && 'transition-colors duration-200 hover:border-line-2',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
      <div>
        <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
        {description ? <p className="mt-0.5 text-[13px] text-ink-3">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * Uma seção de formulário longo: o que se ajusta, com o que aquilo muda escrito
 * embaixo do título.
 *
 * O ícone não decora — é o que dá ao olho um ponto de entrada por seção quando a
 * página tem quatro delas e se procura uma. Um campo "fuso horário" sozinho não
 * diz a ninguém que é ele quem decide quando uma visita conta como vencida.
 */
export function FormSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-sunken">
          <Icon size={16} strokeWidth={1.5} className="text-ink-3" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
          <p className="mt-1 text-[13px] text-ink-3">{description}</p>
          <div className="mt-5">{children}</div>
        </div>
      </div>
    </Card>
  );
}

/** A cor de cada indicador: a do número e a do filete de baixo. */
const KPI_TONE = {
  default: { value: 'text-ink', bar: 'bg-line-2' },
  success: { value: 'text-success', bar: 'bg-success' },
  primary: { value: 'text-primary-ink', bar: 'bg-primary' },
  danger: { value: 'text-danger', bar: 'bg-danger' },
  ink: { value: 'text-ink', bar: 'bg-ink' },
} as const;

export type KpiTone = keyof typeof KPI_TONE;

/**
 * Indicador (design.md §8, Indicador).
 *
 * Quadrado e sem moldura: a superfície sobre a página e, embaixo, um filete de
 * 3px na cor do indicador — cinza no neutro, verde no que vai bem, laranja no
 * que pede atenção, vermelho no que preocupa e tinta no que é referência. Todo
 * indicador tem a mesma forma; o que muda é a cor do filete e do número. O
 * destaque continua vindo da densidade do número, não de fundo colorido.
 */
export function Kpi({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: KpiTone | undefined;
}) {
  const estilo = KPI_TONE[tone];

  return (
    <div className="flex flex-col bg-surface">
      <div className="flex-1 p-5">
        <p className="text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">{label}</p>
        <p className={cn('numeral mt-2.5 text-[28px] leading-none', estilo.value)}>{value}</p>
        {hint ? <p className="mt-2 text-[13px] text-ink-3">{hint}</p> : null}
      </div>

      <span aria-hidden className={cn('h-[3px] shrink-0', estilo.bar)} />
    </div>
  );
}
